import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import {
  ParamsForMethod,
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { toast } from "sonner";

export type Drug = ParamsForMethod<
  "enduser_eligibility_results",
  "develop_health_run_benefit_verification"
>["drugs"][number];

const DEFAULT_DRUGS: Drug[] = [
  {
    name: "Mounjaro",
    dosage: "2.5 mg",
    quantity: 28,
  },
  {
    name: "Ozempic",
    dosage: "0.25 mg",
    quantity: 28,
  },
  {
    name: "Wegovy",
    dosage: "0.25 mg",
    quantity: 28,
  },
];

const EMPTY_DRUG: Drug = {
  name: "",
  dosage: "",
  quantity: 0,
};

const toDrugNames = (
  selectedIds: string[],
  medications: TellescopeModelFromName<"enduser_medications">[] | undefined
): Array<{ name: string }> =>
  selectedIds.reduce<Array<{ name: string }>>((acc, id) => {
    const title = medications?.find((medication) => medication.id === id)?.title;
    if (title) acc.push({ name: title });
    return acc;
  }, []);

/**
 * Resolves the diagnosis multi-select's values back to ICD codes.
 *
 * Keyed by position rather than `EnduserDiagnosis.id`, which is optional — diagnoses synced from an
 * external EHR routinely lack one. Resolving by `id` silently dropped those selections from a billed
 * benefit verification, so the option value in `EligibilitySubmissionForm` must stay the index.
 */
const toDiagnosisCodes = (
  selectedIndexes: string[],
  diagnoses: TellescopeModelFromName<"endusers">["diagnoses"]
): Array<{ code: string }> =>
  selectedIndexes.reduce<Array<{ code: string }>>((acc, index) => {
    const code = diagnoses?.[Number(index)]?.code;
    if (code) acc.push({ code });
    return acc;
  }, []);

export interface UseEligibilitySubmissionFormArgs {
  contact: TellescopeModelFromName<"endusers">;
  onSubmit: () => void;
}

export const useEligibilitySubmissionForm = ({ contact, onSubmit }: UseEligibilitySubmissionFormArgs) => {
  const { t } = useTranslation("contacts");

  const eligibilitySubmissionMutation = useTellescopeMutation({
    model: "enduser_eligibility_results",
    method: "develop_health_run_benefit_verification",

    onSuccess: () => {
      onSubmit();
    },
    onError: () => {
      toast.error(t("There was an error submitting the eligibility check."));
    },
  });

  const providersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {
      filter: {
        NPI: {
          _exists: true,
        },
      },
    },
    select: (data) =>
      data.pages.flat().sort((a, b) => {
        if (contact.assignedTo?.includes(a.id) && !contact.assignedTo?.includes(b.id)) return -1;
        if (!contact.assignedTo?.includes(a.id) && contact.assignedTo?.includes(b.id)) return 1;
        return 0;
      }),
  });

  const medicationsQuery = useTellescopeInfiniteQuery({
    model: "enduser_medications",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => {
      return data.pages.flat().reduce<{
        previous: TellescopeModelFromName<"enduser_medications">[];
        current: TellescopeModelFromName<"enduser_medications">[];
      }>(
        (acc, curr) => {
          if (curr.stoppedTakingAt && curr.stoppedTakingAt.getTime() < Date.now()) {
            acc.previous.push(curr);
          } else {
            acc.current.push(curr);
          }
          return acc;
        },
        { previous: [], current: [] }
      );
    },
  });

  // Held outside `useForm` because the drug list is a list of objects, and the hook's field types
  // top out at `string[]` (`multiselect`). Storing them as JSON strings to fit that shape is what
  // previously forced a `JSON.parse(...) as Drug` on every read. Nothing binds this to a form input
  // — the entries are added and removed through the buttons below — so form state buys nothing here.
  const [selectedDrugs, setSelectedDrugs] = useState<Drug[]>(DEFAULT_DRUGS);

  const form = useForm({
    fields: {
      testSubmissionType: {
        type: "select",
        initialValue: "",
      },
      provider: {
        type: "select",
        initialValue: providersQuery?.data?.length === 1 ? providersQuery.data[0]?.id ?? "" : "",
        required: true,
      },
      insuranceType: {
        // The block renders whenever either policy exists, so defaulting to "Primary" outright would
        // pre-fill a secondary-only contact with a policy they do not hold — and the benefit
        // verification this submits is billed.
        type: "select",
        initialValue: contact.insurance ? "Primary" : "Secondary",
        required: true,
      },
      diagnosisCodes: {
        type: "multiselect",
        initialValue: [],
      },
      currentlyTaking: {
        type: "multiselect",
        initialValue: medicationsQuery.data?.current.map((item) => item.id) ?? [],
      },
      previouslyTaken: {
        type: "multiselect",
        initialValue: medicationsQuery.data?.previous.map((item) => item.id) ?? [],
      },
    },
    validateOn: "change",
    onSubmit: async (fields) => {
      await eligibilitySubmissionMutation.mutateAsync({
        enduserId: contact.id,
        providerUserId: fields.provider,
        insuranceType: fields.insuranceType,
        diagnoses: toDiagnosisCodes(fields.diagnosisCodes, contact.diagnoses),
        drug_history: {
          currently_taking_drugs: toDrugNames(fields.currentlyTaking, medicationsQuery.data?.current),
          previously_taken_drugs: toDrugNames(fields.previouslyTaken, medicationsQuery.data?.previous),
        },
        drugs: selectedDrugs,
        ...(fields.testSubmissionType ? { mock_result: { status: "completed", case: fields.testSubmissionType } } : {}),
      });
    },
  });

  const [drugBeingAdded, setDrugBeingAdded] = useState<Drug>(EMPTY_DRUG);

  const addDrug = () => {
    if (!drugBeingAdded.name) return;
    if (!drugBeingAdded.dosage) return;
    if (!drugBeingAdded.quantity) return;
    // Re-adding a drug already in the list would submit it twice to the billed benefit verification,
    // and the list is keyed by these same fields, so a duplicate is silently declined.
    setSelectedDrugs((previous) =>
      previous.some(
        (drug) =>
          drug.name === drugBeingAdded.name &&
          drug.dosage === drugBeingAdded.dosage &&
          drug.quantity === drugBeingAdded.quantity
      )
        ? previous
        : [...previous, drugBeingAdded]
    );
    setDrugBeingAdded(EMPTY_DRUG);
  };

  const removeDrug = (drugToRemove: Drug) => {
    setSelectedDrugs((previous) => previous.filter((drug) => drug !== drugToRemove));
  };

  return {
    form,
    providers: providersQuery.data,
    currentMedications: medicationsQuery.data?.current,
    previousMedications: medicationsQuery.data?.previous,
    selectedDrugs,
    drugBeingAdded,
    setDrugBeingAdded,
    addDrug,
    removeDrug,
    canSubmit: form.isValid && selectedDrugs.length > 0,
    isSubmitting: eligibilitySubmissionMutation.isPending,
  };
};
